import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useFetch from "../../hooks/useFetch";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
    faHome,
    faCalendar,
    faChartLine,
    faTrophy,
    faBug,
    faVolumeUp,
    faStairs,
} from "@fortawesome/free-solid-svg-icons";

import { faUserAstronaut } from "@fortawesome/free-solid-svg-icons";

import { saveData, getData } from "../../utils/offlineStorage";

import PDDashboard from "./PDDashboard";
import PDDaily from "./PDDaily";
import PDWeekly from "./PDWeekly";
import PDMonthly from "./PDMonthly";
import PDReport from "./PDReport";
import PDTalkingDiary from "./PDTalkingDiary";
import DoorAnimation from "./PDAnimation";
import PDAnimation from "./PDAnimation";
import PDBOSHelp from "./PDBOSHelp";
import PDStoryboard from "./utils/PDStoryboard";

export default function PurpleDoor() {
    const authState = useSelector((state) => state.authReducer);
    const navigate = useNavigate();
    const [fetchData] = useFetch();
    const userId = authState?.user?._id;
    const userCode = authState?.user?.think5whyscode;
    const userName = authState?.user?.name;
    const [subscriptions, setSubscriptions] = useState([]);
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const formatRange = (sub) => {
        if (!sub) return "";
        return `${sub.subscriptionId} (${new Date(sub.startDate).toLocaleDateString()} → ${new Date(sub.endDate).toLocaleDateString()})`;
    };
    const [loadingSubs, setLoadingSubs] = useState(false);
    const [showAI, setShowAI] = useState(false);
    const [showMedals, setShowMedals] = useState(false);
    const [showStreaks, setShowStreaks] = useState(false);
    const [showRewards, setShowRewards] = useState(false);
    const [bosHelp, setBosHelp] = useState(false);
    const [showStory, setShowStory] = useState(false);

    const [useSample, setUseSample] = useState(false);

    const [rewards, setRewards] = useState([]);
    const hasFetchedRewradsRef = useRef(false);

    const hasFetchedSubsRef = useRef(false);
    
    const [tab, setTab] = useState("dashboard");

    const tabs = [
        ["dashboard", "Home", faHome],
        ["daily", "Daily", faCalendar],
        ["weekly", "Weekly", faChartLine],
        ["monthly", "Monthly", faCalendar],
        ["report", "Report", faTrophy],
        // ["growthloop", "GrowthLoop", faStairs],
        ["diary", "Diary", faVolumeUp],
    ];

    const [aiContext, setAiContext] = useState(null);  // AI Context
    const openAI = (type) => { setAiContext(type); setShowAI(true); }; // Central AI Launcher

    const aiRegistry = {
        dashboard: PDDashboard.getAIContext,
        daily: PDDaily.getAIContext,
        weekly: PDWeekly.getAIContext,
        monthly: PDMonthly.getAIContext,
        report: PDReport.getAIContext,
    };

    const go = (value) => {
        setTab(value);

        const builder = aiRegistry[value];
        if (builder) {
            const context = builder(); 
            setAiContext(builder()); // or pass cached tab data
        }
    };

    const [offlineData, setOfflineData] = useState("");
    // useEffect(() => {
    //     const testOffline = async () => {
    //         await saveData("test", {
    //             name: "Purple Door",
    //             date: new Date(),
    //         });

    //         const data = await getData("test");
    //         setOfflineData(JSON.stringify(data));
    //         // console.log("Offline Data:", data);
    //     };

    //     testOffline();
    // }, []);


    const fetchMySubs = async () => {
        try {
            const config = {
                url: "/pd/subscription/my/light",
                method: "post",
                data: { userId, product: "PurpleDoor" },
                headers: { Authorization: authState.token },
            };
            const response = await fetchData(config, { silent: true });

            if (response?.status) {
                const subs = response.data || [];
                setSubscriptions(subs);
                if (subs.length > 0) {
                    setSelectedSubscription(subs[0]);
                }
                hasFetchedSubsRef.current = true;
            }
        } catch (error) {
            console.error("Fetch Subscription Error:", error);
        } finally {
            setLoadingSubs(false);
        }
    };

    useEffect(() => {
        if (userId) fetchMySubs();
    }, [userId]);

    const isExpired = selectedSubscription?.endDate && new Date(selectedSubscription.endDate) < new Date();

    const effectiveIsActive = selectedSubscription?.isActive && !isExpired;

    return (
        <div className="min-h-screen bg-[#0b0f19] text-white flex flex-col">
                <div className="space-y-2 bg-gray-900 p-2 rounded-xl text-white">
                    <div className="flex items-center justify-between p-2 rounded-2xl">
                        <h2 className="text-sm lg:text-lg text-yellow-200 font-bold">
                            ⚡ Think5Whys PurpleDoor
                        </h2>

                        <div className="text-[10px] text-gray-400">
                            {userName}{" - "}{userCode || "No Code"}
                        </div>
                    </div>
                    <div className="flex flex-col lg:flex-row justify-between mb-1">
                        <div className="flex flex-row gap-2">
                            <span className="text-cyan-300 text-xs p-1">
                                📋 Subscription
                            </span>

                            {loadingSubs ? (
                                <span className="text-gray-500 text-[10px]">
                                    Loading...
                                </span>
                            ) : (
                                <select
                                    className="bg-gray-800 text-white text-[10px] px-1 py-1 rounded-md border border-gray-600"
                                    value={selectedSubscription?._id || ""}
                                    onChange={(e) => {
                                        const selected = subscriptions.find(
                                            (s) => s._id === e.target.value
                                        );
                                        setSelectedSubscription(selected || null);
                                    }}
                                    disabled={subscriptions.length === 0}
                                >
                                    {subscriptions.length === 0 ? (
                                        <option value="">No subscriptions found</option>
                                    ) : (
                                        subscriptions.map((sub) => {
                                            const isExpired = new Date(sub.endDate) < new Date();
                                            const status = sub.isActive && !isExpired ? "Active" : "Inactive";
                                            return (
                                                <option key={sub._id} value={sub._id}>
                                                    {sub.product} |{" "}
                                                    {new Date(sub.startDate).toLocaleDateString()} →{" "}
                                                    {new Date(sub.endDate).toLocaleDateString()} |{" "}
                                                    {status === "Active" ? "🟢 Active" : "🔴 Inactive"}
                                                </option>
                                            );
                                        })
                                    )}
                                </select>
                            )}
                        </div>
                        {!selectedSubscription?.isGifted ? ( 
                            <div className="mt-1 rounded-xl border border-pink-500/30 bg-gradient-to-r from-pink-900/30 to-purple-900/30 p-1">
                                <p><span className="text-xs">🎁</span><span className="text-xs text-gray-300">No Gifting Details Available</span></p>
                            </div>
                        ) : (
                            <div className="flex mt-1 rounded-xl border border-pink-500/30 bg-gradient-to-r from-pink-900/30 to-purple-900/30 p-2 justify-center">
                                <div className="flex items-center gap-2 flex-wrap justify-center text-center">
                                    <span className="text-xs">🎁</span>

                                    <p className="text-xs text-gray-300">
                                        Gifted by{" "}
                                        <span className="font-medium text-white">
                                            {selectedSubscription.giftedByName}
                                        </span>

                                        {selectedSubscription?.gifedOn && (
                                            <>
                                                {" "}on{" "}
                                                <span className="text-pink-300">
                                                    {selectedSubscription.gifedOn}
                                                </span>
                                            </>
                                        )}
                                    </p>

                                    {selectedSubscription?.giftMsg && (
                                        <p className="text-xs italic text-pink-200">
                                            "{selectedSubscription.giftMsg}"
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="flex flex-row gap-1 items-right">
                            <button
                                onClick={() => {
                                    setUseSample(true);
                                }}
                                className="w-24 text-yellow-300 hover:text-yellow-200 text-xs lg:text-sm px-1 py-1 rounded-sm"
                                title="Load Sample Dashboard"
                            >
                                📦📊
                            </button>
                        </div>
                    </div>
                </div> 
                <div className="space-y-2 bg-gray-900 p-1 rounded-xl text-white border-t-2 border-cyan-200 mt-1">
                    <div className="grid grid-cols-4 lg:grid-cols-8 bg-[#111827] border-b border-gray-800 gap-0.5">
                        {tabs.map(([id, label, icon]) => (
                            <button
                                key={id}
                                onClick={() => go(id)}
                                className={`w-full flex flex-col text-gray-100 items-center justify-center px-1 py-1 text-[10px] transition-all 

                                ${
                                    tab === id
                                        ? "bg-[#1f2937] text-cyan-400"
                                        : "text-gray-400"
                                }
                                
                                `}
                            >
                                <FontAwesomeIcon icon={icon} className="text-sm mb-0.5"/>
                                <span className="leading-none">{label}</span>
                            </button>
                        ))}
                    </div>
                    <div className="pt-2">
                        {tab === "dashboard" && (
                            <PDDashboard
                                useSample={useSample}
                                go={go}
                                isActiveSub={true}
                                startDate={selectedSubscription?.startDate}
                                endDate={selectedSubscription?.endDate}
                            />
                        )}
                        {tab === "daily" && (
                            <PDDaily
                                useSample={useSample}
                                isActiveSub={effectiveIsActive}
                                subscriptionId={selectedSubscription?.subscriptionId}
                                startDate={selectedSubscription?.startDate}
                                endDate={selectedSubscription?.endDate}
                            />
                        )}
                        {tab === "weekly" && (
                            <PDWeekly
                                useSample={useSample}
                                isActiveSub={effectiveIsActive}
                                startDate={selectedSubscription?.startDate}
                                endDate={selectedSubscription?.endDate}
                            />
                        )}
                        {tab === "monthly" && (
                            <PDMonthly
                                useSample={useSample}
                                isActiveSub={effectiveIsActive}
                                startDate={selectedSubscription?.startDate}
                                endDate={selectedSubscription?.endDate}
                            />
                        )}
                        {tab === "report" && (
                            <PDReport
                                useSample={useSample}
                                isActiveSub={effectiveIsActive}
                                startDate={selectedSubscription?.startDate}
                                endDate={selectedSubscription?.endDate}
                            />
                        )}
                        {tab === "growthloop" && (
                            navigate("/growthloop")
                        )}
                        {tab === "diary" && (
                            <PDTalkingDiary />
                        )}
                    </div>
                </div>
        </div>
    );
}